import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { OneWayInService, SendEmailToCustomerDto } from 'src/api/v2';
import {
  ONE_WAY_IN_CUSTOMER_COMMUNICATION_GET_THREAD_DETAILS,
  ONE_WAY_IN_CUSTOMER_COMMUNICATION_GET_THREADS,
  ONE_WAY_IN_CUSTOMER_COMMUNICATION_SEND_EMAIL,
  ONE_WAY_IN_FORM_FULL_HISTORY,
  ONE_WAY_IN_OPPORTUNITY_BY_ID,
} from 'src/constants/hook-keys';
import { useAuth } from 'src/features/authorization';

const useCustomerEmail = (threadId?: number, formId?: string) => {
  const [service] = useState(() => new OneWayInService());
  const { checkAccess } = useAuth();
  const { read } = checkAccess('OneWayIn');

  const queryClient = useQueryClient();

  const sendEmailMutation = useMutation({
    mutationFn: ({ dto }: { dto: SendEmailToCustomerDto }) => service.sendEmailToCustomer(dto),
    mutationKey: [ONE_WAY_IN_CUSTOMER_COMMUNICATION_SEND_EMAIL],
    onSuccess: () => {
      queryClient.refetchQueries([ONE_WAY_IN_CUSTOMER_COMMUNICATION_GET_THREADS]);
      queryClient.refetchQueries([ONE_WAY_IN_CUSTOMER_COMMUNICATION_GET_THREAD_DETAILS]);
      queryClient.refetchQueries([ONE_WAY_IN_FORM_FULL_HISTORY, formId]);
      queryClient.refetchQueries([ONE_WAY_IN_OPPORTUNITY_BY_ID, formId]);
    },
  });

  const { data, ...rest } = useQuery({
    queryKey: [ONE_WAY_IN_CUSTOMER_COMMUNICATION_GET_THREAD_DETAILS, threadId],
    queryFn: async () => (threadId ? await service.getEmailThreadDetails(threadId) : Promise.reject()),
    enabled: read,
    retry: false,
    staleTime: 1000 * 60 * 5, // 5 min refetching on mount
    refetchOnMount: true,
  });

  return {
    sendEmailMutation,
    details: data,
    ...rest,
  };
};

export default useCustomerEmail;
